import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import EigooMark from "../components/EigooMark";
import { BoardLink } from "../lib/boardLinks";
import {
    ArrowRight,
    ShieldCheck,
    Coins,
    Network,
    Bug,
    Sparkles,
    ExternalLink,
    Rocket,
    Lock,
    Layers,
} from "lucide-react";

const cormorant = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

// Solana brand gradient — used as accent layer over EIGOO obsidian canvas.
const SOLANA_GRADIENT =
    "linear-gradient(95deg, #9945FF 0%, #DC1FFF 35%, #FF6FA1 60%, #14F195 100%)";

const PILLARS = [
    {
        id: "rewards",
        icon: Coins,
        eyebrow: "Pillar / 01",
        title: "Protocol Based Rewards",
        body:
            "EIGOO operatives accrue yield through native Solana protocol economics — not custodial promises. Rewards stream directly from validator inflation and MEV capture. The covenant is on-chain, not on a spreadsheet.",
        spec: ["Validator-attested", "On-chain accrual", "No custody"],
    },
    {
        id: "delegation",
        icon: Network,
        eyebrow: "Pillar / 02",
        title: "Delegating Stake",
        body:
            "Wallet-to-validator delegation routed through the EIGOO Sovereign nodes. Stake remains in your custody. Operators delegate signing weight, not principal — preserving sovereignty while compounding network economic security.",
        spec: ["Self-custody", "Liquid delegation", "Validator-aligned"],
    },
    {
        id: "researchers",
        icon: Bug,
        eyebrow: "Pillar / 03",
        title: "Security Researchers",
        body:
            "Field operatives commissioned to probe Solana protocol surfaces — runtime, BPF programs, RPC pipelines, validator clients. Every disclosed finding routes through the Foundry's bounty ledger with 60/30/10 yield splits.",
        spec: ["Bounty-graded", "Coordinated disclosure", "Foundry-routed"],
    },
];

const POSTURE = [
    { stat: "DSRE", label: "Distributed Security Research Entity" },
    { stat: "80 / 15 / 5", label: "Researcher · Platform · Director" },
    { stat: "0", label: "Custody risk · self-custodied delegation" },
    { stat: "∞", label: "Findings ledger · perpetual residual" },
];

export default function SolanaPortal() {
    useEffect(() => {
        document.title = "Solana Portal · Distributed Security Research | EIGOO";
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) {
            meta = document.createElement("meta");
            meta.name = "description";
            document.head.appendChild(meta);
        }
        meta.content =
            "EIGOO operates as a Distributed Security Research Entity on the Solana Network. Protocol-based rewards, delegated stake, and a coordinated bounty fleet for security researchers.";
    }, []);

    return (
        <div
            className="min-h-screen relative overflow-hidden"
            style={{ background: "hsl(0 0% 3%)" }}
            data-testid="solana-portal-page"
        >
            {/* Solana aurora wash — pink/purple/cyan gradient orbs on obsidian */}
            <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
                <div
                    className="absolute top-[-15%] left-[-10%] w-[55%] h-[55%] rounded-full blur-[140px] opacity-[0.35]"
                    style={{ background: "radial-gradient(circle, #9945FF 0%, transparent 70%)" }}
                />
                <div
                    className="absolute top-[5%] right-[-15%] w-[50%] h-[50%] rounded-full blur-[140px] opacity-[0.30]"
                    style={{ background: "radial-gradient(circle, #DC1FFF 0%, transparent 70%)" }}
                />
                <div
                    className="absolute bottom-[-20%] left-[20%] w-[60%] h-[60%] rounded-full blur-[160px] opacity-[0.22]"
                    style={{ background: "radial-gradient(circle, #14F195 0%, transparent 70%)" }}
                />
                {/* fine grid */}
                <div
                    className="absolute inset-0 opacity-[0.05]"
                    style={{
                        backgroundImage:
                            "linear-gradient(#FF6FA1 1px, transparent 1px), linear-gradient(90deg, #FF6FA1 1px, transparent 1px)",
                        backgroundSize: "48px 48px",
                    }}
                />
            </div>

            {/* Nav */}
            <header className="relative z-10 max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
                <Link to="/" data-testid="solana-logo-link">
                    <EigooMark size="lg" sub="Solana Portal · DSRE" testid="solana-mark" />
                </Link>
                <div className="flex items-center gap-5 text-[10px] uppercase tracking-[0.3em]">
                    <Link
                        to="/foundry"
                        className="text-white/55 hover:text-white transition-colors"
                        data-testid="solana-nav-foundry"
                    >
                        Foundry
                    </Link>
                    <Link
                        to="/intel"
                        className="text-white/55 hover:text-white transition-colors"
                        data-testid="solana-nav-intel"
                    >
                        Intel Hub
                    </Link>
                    <Link
                        to="/login"
                        className="text-white/55 hover:text-white transition-colors"
                        data-testid="solana-nav-login"
                    >
                        Bridge
                    </Link>
                </div>
            </header>

            {/* Hero */}
            <section className="relative z-10 max-w-7xl mx-auto px-6 pt-10 pb-20">
                <div
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-7"
                    style={{
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid rgba(255,111,161,0.4)",
                        boxShadow: "0 0 28px rgba(220,31,255,0.18)",
                    }}
                    data-testid="solana-eyebrow"
                >
                    <span
                        className="h-2 w-2 rounded-full animate-pulse"
                        style={{ background: "#14F195", boxShadow: "0 0 12px #14F195" }}
                    />
                    <span className="text-[10px] uppercase tracking-[0.4em] text-white/85 font-mono">
                        EIGOO × Solana Protocol · DSRE Charter
                    </span>
                </div>

                <h1
                    className="font-heading tracking-tighter leading-[0.92] text-white"
                    style={{ fontSize: "clamp(3rem, 7vw, 6.5rem)" }}
                    data-testid="solana-headline"
                >
                    Distributed{" "}
                    <span
                        className="bg-clip-text text-transparent"
                        style={{ backgroundImage: SOLANA_GRADIENT }}
                    >
                        Security Research
                    </span>{" "}
                    Entity for the Solana Network.
                </h1>

                <p
                    className="mt-7 max-w-3xl text-2xl sm:text-3xl text-white/85 leading-snug"
                    style={{ ...cormorant, fontStyle: "italic" }}
                    data-testid="solana-lede"
                >
                    "We don't speculate on chains. We armor them — and earn protocol-based
                    rewards while doing it."
                </p>

                <div className="mt-9 flex flex-wrap gap-3">
                    <a
                        href="#pillars"
                        data-testid="solana-cta-pillars"
                        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-black hover:scale-[1.02] transition-transform"
                        style={{ background: SOLANA_GRADIENT }}
                    >
                        <Sparkles size={16} /> Read the doctrine <ArrowRight size={16} />
                    </a>
                    <Link
                        to="/foundry/control"
                        data-testid="solana-cta-research"
                        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold border border-white/20 text-white hover:border-[#14F195] hover:text-[#14F195] transition-colors"
                    >
                        <Bug size={16} /> Enlist as researcher
                    </Link>
                    <a
                        href="https://solana.com/news"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="solana-cta-external"
                        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold border border-white/10 text-white/70 hover:text-white hover:border-white/30 transition-colors"
                    >
                        Solana.com <ExternalLink size={14} />
                    </a>
                </div>

                {/* Posture strip */}
                <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-px" data-testid="solana-posture">
                    {POSTURE.map((p) => (
                        <div
                            key={p.label}
                            className="p-5 bg-white/[0.02] border border-white/[0.06]"
                        >
                            <div
                                className="font-heading bg-clip-text text-transparent leading-none"
                                style={{
                                    backgroundImage: SOLANA_GRADIENT,
                                    fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                {p.stat}
                            </div>
                            <div className="mt-2 text-[10px] uppercase tracking-[0.3em] text-white/50">
                                {p.label}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Three Pillars */}
            <section
                id="pillars"
                className="relative z-10 max-w-7xl mx-auto px-6 pb-20"
                data-testid="solana-pillars"
            >
                <div className="flex items-center gap-4 mb-10">
                    <div className="h-px flex-1 bg-white/10" />
                    <div className="text-[10px] uppercase tracking-[0.5em] text-white/60 font-mono">
                        Three Protocol Pillars · Sovereign Charter
                    </div>
                    <div className="h-px flex-1 bg-white/10" />
                </div>

                <div className="grid md:grid-cols-3 gap-5">
                    {PILLARS.map((p) => (
                        <PillarCard key={p.id} pillar={p} />
                    ))}
                </div>
            </section>

            {/* Mission plate */}
            <section className="relative z-10 max-w-7xl mx-auto px-6 pb-20">
                <div
                    data-testid="solana-mission"
                    className="relative overflow-hidden rounded-3xl p-1"
                    style={{ background: SOLANA_GRADIENT }}
                >
                    <div className="rounded-[calc(1.5rem-4px)] bg-[hsl(0_0%_3%)] p-9 lg:p-12 grid lg:grid-cols-3 gap-8 items-start">
                        <div className="lg:col-span-2">
                            <div
                                className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.5em] font-mono mb-4 px-3 py-1 rounded-full"
                                style={{
                                    color: "#FF6FA1",
                                    border: "1px solid rgba(255,111,161,0.3)",
                                }}
                            >
                                <Rocket size={11} /> Mission · DSRE Charter
                            </div>
                            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight leading-[1.05] text-white">
                                The Fleet does not trade Solana.{" "}
                                <span
                                    className="bg-clip-text text-transparent"
                                    style={{ backgroundImage: SOLANA_GRADIENT }}
                                >
                                    The Fleet hardens it.
                                </span>
                            </h2>
                            <p
                                className="mt-5 text-lg text-white/80 leading-relaxed max-w-2xl"
                                style={{ ...cormorant, fontStyle: "italic" }}
                            >
                                "EIGOO operates as a Distributed Security Research Entity — a
                                coordinated fleet of operatives whose only mandate is to deepen
                                the security posture of the Solana protocol. Stake is delegated.
                                Findings are disclosed. Yield is earned through the protocol
                                itself — never extracted from it."
                            </p>
                            <div className="mt-3 text-[10px] uppercase tracking-[0.4em] text-white/55 font-mono">
                                — Directorate · 2026.02
                            </div>
                        </div>

                        {/* Charter checklist */}
                        <ul className="space-y-3 text-sm" data-testid="solana-charter-list">
                            {[
                                ["Self-custody preserved", Lock],
                                ["Validator-aligned delegation", Network],
                                ["Coordinated disclosure", ShieldCheck],
                                ["Bounty-graded research", Bug],
                                ["60/30/10 covenant on findings", Layers],
                            ].map(([label, Ico]) => (
                                <li key={label} className="flex items-center gap-3 text-white/85">
                                    <span
                                        className="h-7 w-7 rounded-full flex items-center justify-center"
                                        style={{
                                            background: "rgba(255,255,255,0.04)",
                                            border: "1px solid rgba(20,241,149,0.4)",
                                        }}
                                    >
                                        <Ico size={13} style={{ color: "#14F195" }} />
                                    </span>
                                    <span>{label}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="relative z-10 max-w-7xl mx-auto px-6 pb-24">
                <div
                    className="rounded-3xl p-9 lg:p-12 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6"
                    style={{
                        background: "rgba(255,255,255,0.02)",
                        border: "1px solid rgba(255,111,161,0.25)",
                        boxShadow: "0 30px 80px -30px rgba(220,31,255,0.4)",
                    }}
                    data-testid="solana-final-cta"
                >
                    <div>
                        <div
                            className="text-[10px] uppercase tracking-[0.5em] font-mono mb-3"
                            style={{ color: "#14F195" }}
                        >
                            Enlist · Sovereign Researcher
                        </div>
                        <div className="font-heading text-3xl sm:text-4xl tracking-tight text-white leading-tight">
                            Bring a finding. Take an orbit.
                        </div>
                        <p className="mt-3 text-sm text-white/65 max-w-xl">
                            Operatives carrying validator badges, low-level Solana runtime
                            knowledge, or BPF program experience receive priority dispatch
                            through the Foundry. The Director reviews every charter brief.
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <Link
                            to="/foundry/control"
                            data-testid="solana-final-charter"
                            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold text-black hover:scale-[1.02] transition-transform"
                            style={{ background: SOLANA_GRADIENT }}
                        >
                            File research brief <ArrowRight size={16} />
                        </Link>
                        <BoardLink
                            data-testid="solana-final-register"
                            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold border border-white/15 text-white hover:border-white/40 transition-colors"
                        >
                            Board the Fleet
                        </BoardLink>
                    </div>
                </div>
                <div className="mt-8 text-[9px] uppercase tracking-[0.4em] text-white/35 font-mono flex justify-between flex-wrap gap-2">
                    <span>EIGOO · DSRE Charter · 2026.02</span>
                    <span>
                        Solana protocol references © Solana Foundation — used under fair
                        attribution
                    </span>
                </div>
            </section>
        </div>
    );
}

function PillarCard({ pillar }) {
    const Icon = pillar.icon;
    return (
        <article
            data-testid={`solana-pillar-${pillar.id}`}
            className="relative group rounded-2xl p-7 border border-white/[0.08] bg-white/[0.02] overflow-hidden hover:border-white/20 transition-colors"
        >
            {/* Top gradient hairline */}
            <span
                aria-hidden="true"
                className="absolute top-0 left-0 right-0 h-px opacity-50 group-hover:opacity-100 transition-opacity"
                style={{ background: SOLANA_GRADIENT }}
            />

            <div
                className="text-[9px] uppercase tracking-[0.5em] font-mono"
                style={{ color: "#FF6FA1" }}
            >
                {pillar.eyebrow}
            </div>

            <div
                className="mt-5 inline-flex items-center justify-center h-12 w-12 rounded-full"
                style={{
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(20,241,149,0.4)",
                }}
            >
                <Icon size={18} style={{ color: "#14F195" }} />
            </div>

            <h3 className="mt-5 font-heading text-3xl tracking-tight text-white leading-tight">
                {pillar.title}
            </h3>

            <p className="mt-4 text-sm text-white/70 leading-relaxed">{pillar.body}</p>

            <div className="mt-6 pt-4 border-t border-white/[0.08] flex flex-wrap gap-x-4 gap-y-1 text-[10px] uppercase tracking-[0.25em] font-mono">
                {pillar.spec.map((s) => (
                    <span key={s} className="flex items-center gap-1.5 text-white/55">
                        <span
                            className="h-1 w-1 rounded-full"
                            style={{ background: "#14F195" }}
                        />
                        {s}
                    </span>
                ))}
            </div>
        </article>
    );
}
